import Button from '../../../sf-components/Button/Button';
import Label from '../../../sf-components/Label/Label';
import TextInput from '../../../sf-components/TextInput/TextInput';
import ButtonStyles from '../../../sf-components/Button/ButtonStyles';
import { WEBHOOKS_EVENTS_SETTINGS, WEBHOOK_EXAMPLE } from './WebhookConsts';
import { useState } from 'react';
import { httpPost } from '../../../utils';
import { Modal } from 'react-bootstrap';
import EventSettingsDrawer from '../../containers/AlertsManagement/components/EventSettingsDrawer';
import AlertsOptionsToggles from '../../containers/AlertsManagement/components/AlertsOptionsToggles';
import { WEBHOOK_TYPES, CLOUD_EVENTS_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { ALPHANUMERIC_REGEX, URL_REGEX } from '@silverfort-nodelibs/common/utils/consts';
import { Icon, Icons } from '@silverfort/sf-components';
import { SUBCATEGORIES } from '../../common/categories';
import { useIsCloudOnly } from '../../hooks/featureFlags/useIsCloudOnly';

const { WEBHOOK } = SUBCATEGORIES;

const WebhookDetails = ({ data, otherWebhooks, onSave, onCancel, isEdit = false }) => {
    const [webhookData, setWebhookData] = useState(data);
    const [eventSelections, setEventSelections] = useState();
    const [eventInEdit, setEventInEdit] = useState();
    const [nameError, setNameError] = useState();
    const [urlError, setUrlError] = useState();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const isCloudOnly = useIsCloudOnly();

    const validateName = (name) => {
        if (!name) return 'Mandatory field';
        if (otherWebhooks.find((webhook) => webhook.name === name && webhook.id !== webhookData.id))
            return 'Name already exists';
        if (!ALPHANUMERIC_REGEX.test(name)) return 'Use alphanumeric characters only';
    };

    const validateUrl = (url) => {
        if (!URL_REGEX.test(url)) return 'Invalid URL';
    };

    const sendProdData = (data) => {
        httpPost('/settings/sendWebhookProductData', { data, isEdit });
    };

    return (
        <div className="font-sf flex flex-col p-6 w-full h-92 border border-sf-gray-100 bg-white rounded-lg">
            <div className="mb-4 flex items-center bg-sf-gray-50 pl-6 h-20 border border-sf-gray-100 rounded-lg">
                <Icon className="text-sf-gray-400" icon={Icons.Webhook} size={36} />
                <div className="ml-2 text-sm">
                    <div className="font-medium text-sf-gray-800">{WEBHOOK.NAME}</div>
                    <div className="font-normal text-sf-gray-500">
                        For more information about this integration and the required prerequisites, see
                        <a
                            className="ml-1 text-sf-primary-500"
                            href="https://docs.silverfort.com/docs/webhooks"
                            target="_blank"
                            rel="noopener noreferrer">
                            here
                        </a>
                        .
                    </div>
                </div>
            </div>
            <div className="flex p-2">
                <div className="w-1/5 h-10 flex items-center">
                    <Label text={'Webhook name'} mandatory={true} />
                </div>
                <div className="w-4/5 h-10 mb-1">
                    <TextInput
                        ValidationMessage={nameError}
                        placeholder={'Add webhook name'}
                        value={webhookData.name}
                        onChange={(val) => {
                            setNameError();
                            setWebhookData({ ...webhookData, name: val });
                        }}
                        className={'w-72'}
                    />
                </div>
            </div>
            <div className="flex p-2">
                <div className="w-1/5 h-10 flex items-center">
                    <Label text={'Webhook URL'} mandatory={true} />
                </div>
                <div className="w-4/5 h-10 mb-1">
                    <TextInput
                        ValidationMessage={urlError}
                        placeholder={'Add webhook URL'}
                        value={webhookData.url}
                        onChange={(val) => {
                            setUrlError();
                            setWebhookData({ ...webhookData, url: val });
                        }}
                        className={'w-[576px]'}
                    />
                </div>
            </div>

            <div className="flex py-4 px-2">
                <div className="w-1/5 h-10 flex items-center">
                    <Label text={'Events'} />
                </div>
                <div className="flex w-4/5 flex-col mt-0.5">
                    {!isCloudOnly && (
                        <AlertsOptionsToggles
                            alertType="Webhook"
                            data={webhookData}
                            setData={setWebhookData}
                            eventInEdit={eventInEdit}
                            setEventInEdit={setEventInEdit}
                            eventsTypes={WEBHOOK_TYPES}
                            title={'On prem events'}
                        />
                    )}
                    <AlertsOptionsToggles
                        alertType="Webhook"
                        data={webhookData}
                        setData={setWebhookData}
                        eventInEdit={eventInEdit}
                        setEventInEdit={setEventInEdit}
                        eventsTypes={CLOUD_EVENTS_TYPES}
                        title={'Cloud events'}
                    />
                </div>
            </div>
            <div className="flex justify-end items-center h-8">
                <Button
                    text={'Cancel'}
                    buttonStyle={ButtonStyles.TertiarySolid}
                    onClick={() => (data !== webhookData ? setShowCancelModal(true) : onCancel())}
                    className="mr-1"
                />
                <Button
                    text={'Save'}
                    disabled={data === webhookData}
                    onClick={() => {
                        const nameValidation = validateName(webhookData.name);
                        const urlValidation = validateUrl(webhookData.url);
                        if (nameValidation || urlValidation) {
                            setNameError(nameValidation);
                            setUrlError(urlValidation);
                        } else {
                            setNameError();
                            setUrlError();
                            const webhookDataToSave = {
                                ...webhookData,
                                lastModified: Date.now()
                            };
                            onSave(webhookDataToSave);
                            sendProdData(webhookDataToSave);
                        }
                    }}
                />
            </div>
            <EventSettingsDrawer
                alertType={'webhook'}
                data={webhookData}
                eventInEdit={eventInEdit}
                eventSelections={eventSelections}
                eventSettings={WEBHOOKS_EVENTS_SETTINGS[eventInEdit]}
                example={WEBHOOK_EXAMPLE}
                setEventSelections={setEventSelections}
                setEventInEdit={setEventInEdit}
                setData={setWebhookData}
            />
            {showCancelModal && (
                <Modal
                    className="font-sf flex flex-col justify-center"
                    id={'cancelModal'}
                    dialogClassName="w-[360px] [&_.modal-content]:rounded-lg [&_.modal-content]:shadow-modal-light"
                    show
                    onHide={() => setShowCancelModal(false)}
                    keyboard={false}
                    backdrop="static">
                    <Modal.Header className="border-hidden pt-4 pr-6 pb-0 pl-0" closeButton>
                        <div className="px-4 pb-4 mb-1 text-14 font-medium text-sf-gray-800">Discard changes?</div>
                    </Modal.Header>
                    <Modal.Body className=" text-13 px-4 pb-6 h-full border-t border-b">
                        Any updates you made will be lost.
                    </Modal.Body>
                    <Modal.Footer className="border-hidden px-6">
                        <div className="flex justify-end items-center">
                            <Button
                                onClick={() => setShowCancelModal(false)}
                                text={'No'}
                                buttonStyle={ButtonStyles.TertiarySolid}
                                className="mr-2"
                            />
                            <Button onClick={onCancel} text={'Yes'} />
                        </div>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};

export default WebhookDetails;
